import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Skeleton,
    Avatar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Badge
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
// Assume you have a hook to fetch all customers
import { useAdminAllCustomers } from 'hooks/useCustomers';
import FilterDrawer from 'components/common/FilterDrawer';
import PermissionGuard from 'ui-component/PermissionGuard';
import { FilterList } from '@mui/icons-material';

export default function CustomerList() {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');

    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState({});

    // 🔹 Fetch Customers
    // Adjust hook/service to fit your API structure (page, limit, search)
    //
    const {
        data: customerData,
        isLoading,
        error
    } = useAdminAllCustomers({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        ...filters
    });

    const handleFilterApply = (newFilters) => {
        setFilters(newFilters);
        setPage(0);
    };

    const filterConfig = [
        { name: 'startDate', label: 'Start Date', type: 'date' },
        { name: 'endDate', label: 'End Date', type: 'date' },
        {
            name: 'sortBy',
            label: 'Sort By',
            type: 'select',
            options: [
                { label: 'Newest', value: 'createdAt' },
                { label: 'Name', value: 'firstName' }
            ]
        },
        {
            name: 'sortOrder',
            label: 'Sort Order',
            type: 'select',
            options: [
                { label: 'Descending', value: 'desc' },
                { label: 'Ascending', value: 'asc' }
            ]
        }
    ];

    const customers = customerData?.data || [];
    const total = customerData?.total || 0;
    const skeletonRows = Array.from({ length: rowsPerPage });

    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box p={3} sx={{ background: '#f5f6fa', minHeight: '100vh' }}>
            <Typography variant="h4" fontWeight={700} mb={4}>
                Customer Management
            </Typography>

            <Paper sx={{ width: '100%', overflow: 'hidden', padding: 2 }}>
                {/* Filters */}
                <Stack direction="row" spacing={2} mb={2} alignItems="center">
                    <TextField
                        label="Search Name, Email, or Phone"
                        variant="outlined"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        size="small"
                        sx={{ width: '350px' }}
                    />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status"
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="ALL">All Statuses</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                            <MenuItem value="banned">Banned</MenuItem>
                        </Select>
                    </FormControl>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2 }}>
                        <Badge badgeContent={Object.keys(filters).length} color="primary">
                            <Button variant="outlined" startIcon={<FilterList />} onClick={() => setFilterOpen(true)}>
                                Filters
                            </Button>
                        </Badge>
                        {Object.keys(filters).length > 0 && (
                            <Button variant="text" onClick={() => setFilters({})} color="error" size="small">
                                Reset
                            </Button>
                        )}
                    </Stack>
                    <Box sx={{ flexGrow: 1 }} />
                </Stack>
                {/* Table */}
                <TableContainer sx={{ maxHeight: 600 }}>
                    <Table stickyHeader aria-label="customer table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Customer Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Status</TableCell>
                                {/* <TableCell>Total Orders</TableCell> */}
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                skeletonRows.map((_, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>
                                            <Skeleton width={150} />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton width={200} />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton width={120} />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton width={80} />
                                        </TableCell>
                                        {/* <TableCell>
                                            <Skeleton width={50} />
                                        </TableCell> */}
                                        <TableCell align="right">
                                            <Skeleton width={60} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : customers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No customers found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                customers.map((customer) => (
                                    <TableRow hover key={customer.id}>
                                        <TableCell>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Avatar>{customer.name?.[0] || 'C'}</Avatar>
                                                <Typography variant="subtitle2">
                                                    {customer.firstName} {customer.lastName}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{customer.email}</TableCell>
                                        <TableCell>{customer.phoneNumber}</TableCell>
                                        <TableCell>
                                            {customer.isBanned ? (
                                                <Chip label={'Banned'} color={'error'} size="small" />
                                            ) : (
                                                <Chip
                                                    label={customer.status === 'active' ? 'Active' : 'Inactive'}
                                                    color={customer.status === 'active' ? 'success' : 'error'}
                                                    size="small"
                                                />
                                            )}
                                        </TableCell>
                                        {/* <TableCell>{customer.totalOrders || 0}</TableCell> */}
                                        <TableCell align="right">
                                            <PermissionGuard permission="manage_users" action="view">
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    //    send id to useParams
                                                    onClick={() => navigate(`/admin/customers/${customer.id}`)}
                                                >
                                                    View
                                                </Button>
                                            </PermissionGuard>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {/* Pagination */}
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <FilterDrawer
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                onApply={handleFilterApply}
                initialFilters={filters}
                config={filterConfig}
            />
        </Box>
    );
}
